import { EAlertSeverity } from "../../../../shared/oversight-core/enums/alert-severity";
import { EAlertState } from "../../../../shared/oversight-core/enums/alert-state";
import { IAlertOptions } from "../../../../shared/oversight-core/interfaces/alert-options";
import AppSelect, {
  Option,
} from "../../../../shared/oversight-core/ui-elements/app-select/app-select";

export interface IAlertFilterValues {
  severity: EAlertSeverity[];
  alertState: EAlertState;
  alertName: string[];
}

export const severityLabels: Record<EAlertSeverity, string> = {
  [EAlertSeverity.CRITICAL]: "Critical",
  [EAlertSeverity.WARNING]: "Warning",
  [EAlertSeverity.INFO]: "Info",
};

const stateOptions: Option[] = [
  { value: EAlertState.OPEN, label: "Open" },
  { value: EAlertState.CLOSED, label: "Closed" },
  { value: EAlertState.ALL, label: "Open and closed" },
];

// How many of these filters differ from their defaults, for the filter button's badge.
export const countAlertFilters = (
  values: IAlertFilterValues,
  defaultState: EAlertState,
): number =>
  (values.severity.length > 0 ? 1 : 0) +
  (values.alertName.length > 0 ? 1 : 0) +
  (values.alertState !== defaultState ? 1 : 0);

interface IProps {
  values: IAlertFilterValues;
  onChange: (values: IAlertFilterValues) => void;
  options?: IAlertOptions;
}

// The alert filters both error logs share: status, severity and alert type. The type options come
// from the server (alertOptions), so they only offer what has actually occurred.
const AlertFilterFields = (props: IProps) => {
  const { values, onChange, options } = props;

  const severityOptions: Option[] = (
    options?.severities?.length
      ? options.severities
      : Object.values(EAlertSeverity)
  ).map((severity) => ({ value: severity, label: severityLabels[severity] }));

  // A chosen type stays listed even if the latest answer no longer contains it.
  const alertNames = Array.from(
    new Set([...(options?.alertNames ?? []), ...values.alertName]),
  );
  const alertNameOptions: Option[] = alertNames.map((name) => ({
    value: name,
    label: name,
  }));

  const toValues = (selected: Option | Option[] | null): string[] =>
    ((selected as unknown as Option[] | null) ?? []).map(
      (option) => option.value,
    );

  return (
    <>
      <AppSelect
        className="text-light font-size-14"
        label="Status"
        options={stateOptions}
        selectedValue={stateOptions.find(
          (option) => option.value === values.alertState,
        )}
        onChangeOption={(selected) =>
          onChange({ ...values, alertState: selected.value as EAlertState })
        }
        isRequired={false}
        labelFontSize="font-size-14"
      />
      <AppSelect
        className="text-light font-size-14 mt-4"
        label="Severity"
        placeholder="All severities"
        options={severityOptions}
        selectedValue={severityOptions.filter((option) =>
          values.severity.includes(option.value as EAlertSeverity),
        )}
        onChangeOption={(selected) =>
          onChange({
            ...values,
            severity: toValues(selected) as EAlertSeverity[],
          })
        }
        isMultiSelect
        closeMenuOnSelect={false}
        isRequired={false}
        labelFontSize="font-size-14"
      />
      <AppSelect
        className="text-light font-size-14 mt-4"
        label="Alert Type"
        placeholder="All alert types"
        options={alertNameOptions}
        selectedValue={alertNameOptions.filter((option) =>
          values.alertName.includes(option.value),
        )}
        onChangeOption={(selected) =>
          onChange({ ...values, alertName: toValues(selected) })
        }
        isMultiSelect
        closeMenuOnSelect={false}
        isRequired={false}
        labelFontSize="font-size-14"
      />
    </>
  );
};

export default AlertFilterFields;
